import type { ReactNode } from 'react'

export interface CheckoutSectionProps {
  /** The badge number, computed by the caller — `1 | 2 | 3` on a normal
   * checkout, `1 | 2 | 3 | 4` once the RFQ multi-lead-time delivery-plan
   * step (`resolveShowDeliveryPlan`) inserts itself between §1 and §2. */
  readonly number: number
  readonly heading: string
  /** Stable id so the numbered badge + heading can be referenced by
   * `aria-labelledby` on the section landmark below. */
  readonly headingId: string
  readonly children: ReactNode
}

/**
 * One numbered section of the single-page checkout (Stage-2b) — a filled
 * indigo circle badge ("1"/"2"/"3") + an `<h2>` question heading, both inside
 * a `<section>` landmark labelled by that heading. Replaces the former
 * accordion (`CheckoutStepShell`, UID-60): ALL THREE sections render fully
 * expanded at once, side by side with the sticky order-summary rail, per the
 * approved single-page design — there is no active/collapsed state and no
 * per-section "Continue"/"Edit" affordance anymore.
 */
export function CheckoutSection({ number, heading, headingId, children }: CheckoutSectionProps) {
  return (
    <section className="checkout-section" aria-labelledby={headingId}>
      <div className="checkout-section-header">
        <span className="checkout-section-badge" aria-hidden="true">
          {number}
        </span>
        <h2 id={headingId} className="checkout-section-heading">
          {heading}
        </h2>
      </div>
      <div className="checkout-section-body">{children}</div>
    </section>
  )
}
